/**
 * SystemDoxHeader — the one canonical top header for every SystemDox surface.
 *
 * A single-row, full-width bar with three zones:
 *   [ logo + brand + SurfaceSwitcher ] · [ search ] · [ notifications + account ]
 *
 * Equal flex-1 flanks with a fixed-width centre keep the search page-centred;
 * the left flank can shrink (min-w-0) and the right flank is shrink-0, so the
 * account controls never overlap the switcher when the header gets tight.
 *
 * This component owns only the header *content*. Wrap it in a header element
 * that supplies the chrome (border + bg + `px-4 py-3` height) — either the
 * shared AppShell header (Console) or CaptureShell's own header — so the bar
 * has identical dimensions and layout on every surface. The static docs portal
 * (Starlight `Header.astro`) restates the same layout/dimensions in Astro.
 *
 * Search and notifications are opt-in: surfaces without a global search index
 * or a notification provider (e.g. the capture app) hide those zones, but the
 * three-zone geometry — and therefore the header's dimensions — is unchanged.
 *
 * The brand (logo + wordmark) links to the tenant's launch home
 * ({slug}.systemdox.com) whenever the tenant slug is known.
 *
 * `signedOut` is the public variant for the auth pages (/login, /signup):
 * no surface switcher or account controls — Sign in / Sign up links take the
 * right flank instead — but the three-zone geometry is unchanged.
 */

import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { NotificationBell } from '@puglieseweb/shared-react-notifications';
import { ProjectSelector, hasSpaceTier } from '@systemdox/ui';
import { useTenant } from '@/contexts/TenantContext';
import { useProject } from '@/contexts/ProjectContext';
import { SurfaceSwitcher } from '@/components/layout/SurfaceSwitcher';
import { UserMenu } from '@/components/layout/UserMenu';
import { SearchBar } from '@/components/search/SearchBar';
import { launchUrl, getTenantSlug, rememberedTenantSlug } from '@/utils/subdomain';

export interface SystemDoxHeaderProps {
  /** Show the centred global search box (needs the search index). */
  showSearch?: boolean;
  /** Show the notification bell (needs a NotificationProvider). */
  showNotifications?: boolean;
  /** Click handler for the notification bell. */
  onNotificationsClick?: () => void;
  /** Adds a Settings link to the account menu. */
  settingsTo?: string;
  /** Adds a Notifications (full history) link to the account menu. */
  notificationsTo?: string;
  /** Public (signed-out) variant for the auth pages: hides the surface
      switcher and account controls, offers Sign in / Sign up instead. */
  signedOut?: boolean;
}

export function SystemDoxHeader({
  showSearch = false,
  showNotifications = false,
  onNotificationsClick,
  settingsTo,
  notificationsTo,
  signedOut = false,
}: SystemDoxHeaderProps) {
  // Slug resolution mirrors SurfaceSwitcher: token tenant, then hostname, then
  // the cross-subdomain cookie (so the capture app, which has neither, still
  // resolves it). The brand links to that tenant's launch home.
  const { tenant } = useTenant();
  const projectCtx = useProject();
  const slug = tenant?.slug ?? getTenantSlug() ?? rememberedTenantSlug();
  const homeUrl = slug ? launchUrl(slug) : null;

  const brand = (
    <>
      <img src="/systemdox-logo.png" alt="" className="h-7 w-7 shrink-0 object-contain rounded-lg" />
      {/* Wordmark visibility mirrors the docs portal's .sdx-wordmark bands
          (custom.css) so the two headers agree at every width: shown from sm,
          hidden again in the lg→82rem band where the centred search crowds the
          left flank, shown once past 82rem. overflow-clip on the link is the
          safety net — the wordmark never bleeds under the shrink-0 switcher. */}
      <span className="sdx-brand-text hidden whitespace-nowrap bg-gradient-to-r from-blue-600 via-teal-500 to-emerald-500 bg-clip-text text-lg font-bold text-transparent sm:block lg:hidden min-[82rem]:block">
        SystemDox
      </span>
    </>
  );

  return (
    <div className="flex w-full items-center gap-3">
      {/* Left: brand (links to the tenant launch home) + surface switcher */}
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {homeUrl ? (
          <a
            href={homeUrl}
            title="SystemDox home"
            className="flex min-w-0 items-center gap-3 overflow-clip rounded-md transition-opacity hover:opacity-80"
          >
            {brand}
          </a>
        ) : (
          <span className="flex min-w-0 items-center gap-3 overflow-clip">{brand}</span>
        )}
        {!signedOut && <SurfaceSwitcher className="shrink-0" />}
      </div>
      {/* Centre: search — hidden on mobile / when there isn't room */}
      {showSearch && (
        <div className="hidden shrink-0 lg:block">
          <SearchBar expanded className="w-80 xl:w-96" />
        </div>
      )}
      {/* Right: scope selector + notifications + account — pinned, never
          shrinks. The selector sits between the search control and the bell
          (project-selector-surfaces spec) and hides entirely while the caller
          has no visible projects and at most one space (dormancy) — the
          space tier alone justifies it in a multi-space tenant with no
          projects yet (scope-selector spec). */}
      <div className="flex flex-1 shrink-0 items-center justify-end gap-2">
        {signedOut ? (
          <>
            <Link
              to="/login"
              className="rounded-md px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
            >
              Sign in
            </Link>
            <Link
              to="/signup"
              className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Sign up
            </Link>
          </>
        ) : (
          <>
            {(projectCtx.projects.length > 0 || hasSpaceTier(projectCtx.spaces)) && (
              <ProjectSelector
                projects={projectCtx.projects}
                total={projectCtx.total}
                selected={projectCtx.selected}
                onSelect={projectCtx.select}
                onSearch={projectCtx.search}
                spaces={projectCtx.spaces}
                selectedSpace={projectCtx.selectedSpace}
                onSelectSpace={projectCtx.selectSpace}
                loading={projectCtx.loading}
              />
            )}
            {/* Mobile-only search trigger — the centred search box is hidden
                below lg, so this magnifier gives mobile a way into the same
                overlay. */}
            {showSearch && <SearchBar iconOnly className="lg:hidden" />}
            {showNotifications && <NotificationBell onClick={onNotificationsClick} />}
            {/* Notification history — the bell panel shows recent activity;
                this goes to the full, durable list. Gated on notificationsTo
                as well as the bell so surfaces without that route (the capture
                host) don't get a dead-end link. h-9/w-9 matches the other
                header controls and keeps the bar height unchanged. */}
            {showNotifications && notificationsTo && (
              <Link
                to={notificationsTo}
                title="Notification history"
                aria-label="Notification history"
                className="flex h-9 w-9 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              >
                <History className="h-5 w-5" />
              </Link>
            )}
            <UserMenu settingsTo={settingsTo} notificationsTo={notificationsTo} />
          </>
        )}
      </div>
    </div>
  );
}
